// Gest-familien: Escape, Delete og Ctrl/⌘+Z under ÉN avtale.
//
// HVORFOR DENNE FILEN FINNES (UX-rapporten 2026-08-14, §4b):
//
//   «Escape har åtte eiere og ingen prioritetsavtale. Fem lyttere på `document`,
//    tre på `window`. Fordi `document` bobler først, vinner PANEL-LUKKING over
//    VERKTØY-AVBRUDD. Med et panel åpent avbryter Escape derfor ikke verktøyet —
//    mens modus-stripa samtidig lover «Avslutt (Esc)». Løftet er usant nettopp
//    når operatøren trenger det.»
//
// Og videre: «`shortcuts.ts` avviser alt med meta/ctrl/alt, så ⌘Z kan ikke nå
// noe. Ingen Delete- eller Backspace-binding finnes globalt. Sammen med at `×`
// er tvetydig og at bekreftelse mangler på alt destruktivt, betyr det at det
// ikke finnes noe sikkerhetsnett bak en eneste destruktiv handling i COP-en.»
//
// GREPET: én lytter i KAPRE-fasen på `window`. Den kjører før alle de åtte
// boblende lytterne. Tar avtalen tasten, stopper den forplantningen — og de
// andre ser den aldri. Tar den den IKKE (ingen verktøy aktivt, ingenting merket),
// forplanter tasten seg som før, og panel-lukkingen i `WindowManager` virker
// uendret.
//
// Det er derfor dette IKKE er en niende lytter: den er en PORT foran de åtte, med
// en prioritet som kan leses på én skjerm og testes uten DOM.
//
//   modal → verktøy → seleksjon → (videre til panel/overlegg)
//
// En åpen modal stopper ALT: modalens eget Escape skal avbryte den, og et
// verktøy skal ikke avbrytes av en tast operatøren mente for dialogen foran.

/** Ett ledd i prioritetskjeden. `kan()` må være billig og uten bivirkning. */
export interface Gestkrav {
  /** Navn til feilsøking og test — aldri vist til operatøren. */
  navn: string;
  /** Har dette leddet noe å gjøre nå? */
  kan: () => boolean;
  /** Gjør det. Kalles bare når `kan()` er sann. */
  utfor: () => void;
}

/**
 * Finn det første leddet som har noe å gjøre. Ren funksjon → prioriteten kan
 * testes uten DOM, uten kart og uten paneler.
 */
export function forsteKrav(kjede: readonly Gestkrav[]): Gestkrav | null {
  for (const k of kjede) {
    if (k.kan()) return k;
  }
  return null;
}

/** Hvilken gest en tast er, eller `null` når den ikke er vår. */
export type Gesttype = 'escape' | 'slett' | 'angre' | 'gjenta';

/** Det avtalen trenger å vite om et tastetrykk. Duck-typet → testbart uten DOM. */
export interface Tastetrykk {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
}

/**
 * Hvilken gest er dette? Ren funksjon, så tast-avbildningen kan testes uten
 * nettleser. (Testene i denne pakka kjører i node — se `ui/windows.test.ts`.)
 */
export function gestForTast(e: Tastetrykk): Gesttype | null {
  const meta = e.metaKey === true || e.ctrlKey === true;
  const k = e.key.toLowerCase();
  if (!meta && e.key === 'Escape') return 'escape';
  if (!meta && (e.key === 'Delete' || e.key === 'Backspace')) return 'slett';
  if (meta && k === 'z') return e.shiftKey === true ? 'gjenta' : 'angre';
  if (meta && k === 'y') return 'gjenta';
  return null;
}

/** Det avtalen trenger å vite om et element. Duck-typet → testbart uten DOM. */
export interface Tastemal {
  tagName?: string;
  type?: string;
  isContentEditable?: boolean;
}

/** Elementtyper der et tastetrykk hører til TEKSTEN, ikke til kartet. */
export function erSkrivefelt(mal: Tastemal | EventTarget | null): boolean {
  const el = mal as Tastemal | null;
  if (!el || typeof el.tagName !== 'string') return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  // Avkryssing, radio og knapper er IKKE skriving — Delete og Escape skal virke
  // der. (Samme skille som `ui/windows.ts` gjør for Escape; holdt likt bevisst.)
  const type = el.type?.toLowerCase() ?? 'text';
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(type);
}

/** Er en blokkerende dialog åpen? `bekreft()`/`modalEl()` legger denne i body. */
export function modalApen(): boolean {
  return document.querySelector('.skade-modal-overlay') !== null;
}

export interface Gestavtale {
  /** Escape, i prioritert rekkefølge. Tomt ledd → tasten går videre. */
  escape: Gestkrav[];
  /** Delete/Backspace på det som er merket. */
  slett: Gestkrav[];
  /** Ctrl/⌘+Z. */
  angre: Gestkrav[];
  /** Ctrl/⌘+Shift+Z (og ⌘Y). */
  gjenta?: Gestkrav[];
  /** Overstyrbar for test — ellers leses DOM. */
  erModalApen?: () => boolean;
}

/**
 * Monter avtalen. Returnerer en avmonterings-funksjon (til test).
 *
 * ⚠ `capture: true` er ikke en detalj — det er hele mekanismen. Uten den ville
 * dette blitt lytter nummer ni, og `document`-lytterne ville fortsatt vunnet.
 */
export function installerGester(avtale: Gestavtale): () => void {
  const erModal = avtale.erModalApen ?? modalApen;

  const paaTast = (e: KeyboardEvent) => {
    // En dialog foran eier tasten. Både Escape (avbryt dialogen) og Delete
    // (i et felt inne i dialogen) skal tilhøre den, ikke kartet bak.
    if (erModal()) return;
    if (erSkrivefelt(e.target)) return;

    const gest = gestForTast(e);
    if (!gest) return;
    const kjede: readonly Gestkrav[] = gest === 'escape' ? avtale.escape
      : gest === 'slett' ? avtale.slett
        : gest === 'angre' ? avtale.angre
          : (avtale.gjenta ?? []);

    const krav = forsteKrav(kjede);
    if (!krav) return; // ingenting å gjøre → tasten går videre til panelene
    e.preventDefault();
    // `stopPropagation` alene er ikke nok: flere av de gamle lytterne henger på
    // `document` og `window` i SAMME fase, og de ville fortsatt kjørt.
    e.stopImmediatePropagation();
    krav.utfor();
  };

  window.addEventListener('keydown', paaTast, { capture: true });
  return () => window.removeEventListener('keydown', paaTast, { capture: true });
}
